import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import proxy from "../../proxy";

import ltcLogo from "../../assets/ltc_icon.png";
import bchLogo from "../../assets/bch_icon.png";
import dogeLogo from "../../assets/doge_icon.png";
import dashLogo from "../../assets/dash_logo.png";
import polLogo from "../../assets/pol_logo.png";
import zecLogo from "../../assets/zec_logo.png";

import {
  Box,
  Text,
  Image,
  Button,
  Link,
  VStack,
  HStack,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Skeleton,
} from "@chakra-ui/react";
import { ExternalLinkIcon, CopyIcon, CheckIcon } from "@chakra-ui/icons";

const LOGO = {
  ltclogo: ltcLogo,
  bchlogo: bchLogo,
  dogelogo: dogeLogo,
  dashlogo: dashLogo,
  pollogo: polLogo,
  zeclogo: zecLogo,
};

const COLORS = {
  LTC: { color: "#345D9D", gradient: "linear(to-br, #345D9D, #5A95D8)", shadow: "rgba(52, 93, 157, 0.4)" },
  BCH: { color: "#8DC351", gradient: "linear(to-br, #8DC351, #B8E986)", shadow: "rgba(141, 195, 81, 0.4)" },
  DOGE: { color: "#C3A634", gradient: "linear(to-br, #C3A634, #F0D365)", shadow: "rgba(195, 166, 52, 0.4)" },
  DASH: { color: "#008DE4", gradient: "linear(to-br, #008DE4, #40B5F5)", shadow: "rgba(0, 141, 228, 0.4)" },
  POL: { color: "#8247E5", gradient: "linear(to-br, #8247E5, #A87EF0)", shadow: "rgba(130, 71, 229, 0.4)" },
  ZEC: { color: "#ECB244", gradient: "linear(to-br, #ECB244, #F5D078)", shadow: "rgba(236, 178, 68, 0.4)" },
  DEFAULT: { color: "#38B2AC", gradient: "linear(to-br, #38B2AC, #4FD1C5)", shadow: "rgba(56, 178, 172, 0.4)" },
};

const useWallet = (address) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!address) return;
    axios
      .post(`${proxy}server_mts/GetWallet.php`, { address })
      .then((res) => {
        if (res.data?.length) setWallet(res.data[0]);
        else throw new Error("Invalid response");
      })
      .catch(() => setError("Unable to retrieve balance."))
      .finally(() => setLoading(false));
  }, [address]);

  return { wallet, loading, error };
};

export default function WalletCard({ address, logo }) {
  const { wallet, loading, error } = useWallet(address);
  const toast = useToast();
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [balanceRevealed, setBalanceRevealed] = useState(false);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardMaxW = useBreakpointValue({ base: "160px", sm: "280px", md: "280px" });
  const cardPadding = useBreakpointValue({ base: 2, sm: 3, md: 3.5 });
  const logoSize = useBreakpointValue({ base: "32px", sm: "40px", md: "42px" });

  const color = wallet?.symbol && COLORS[wallet.symbol] ? COLORS[wallet.symbol] : COLORS.DEFAULT;
  const logoPath = LOGO[logo];

  const walletData = useMemo(() => {
  if (!wallet) return { amount: 0, price: 0, totalUsd: 0 };
  const amount = Number(wallet.amount || 0);
  const price = Number(wallet.price || 0);
  return {
    amount,
    price,
    totalUsd: amount * price,
  };
}, [wallet]);

  useEffect(() => {
    if (!loading && wallet) {
      setTimeout(() => setBalanceRevealed(true), 200);
    }
  }, [loading, wallet]);

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const raf = useRef(null);

  const handleMouseMove = (e) => {
    if (isMobile || !cardRef.current) return;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const rect = cardRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 8;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * -8;
      setTilt({ x, y });
    });
  };

  const copy = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      toast({
        title: "Copied!",
        status: "success",
        position: "top",
        duration: 1500,
      });
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const truncate = (a) => (a ? `${a.slice(0, 8)}...${a.slice(-8)}` : "");

  const explorer = wallet?.explorer
    ? `https://blockchair.com/${wallet.explorer}/address/${address}`
    : "#";

if (loading) {
  return (
    <Box
      borderRadius={{ base: "lg", md: "xl" }}
      p={cardPadding}
      bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
      border="1px solid"
      borderColor="whiteAlpha.200"
      maxW={cardMaxW}
      mx="auto"
      overflow="hidden"
      position="relative"
    >
      {/* Shimmer animation - adaugă în style tag sau în tema Chakra */}
      <style>
        {`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}
      </style>

      <VStack spacing={2}>
        {/* Header skeleton cu shimmer */}
        <HStack w="full" justify="space-between">
          <HStack spacing={2}>
            {/* Logo skeleton */}
            <Box
              position="relative"
              overflow="hidden"
              borderRadius="lg"
              bg="whiteAlpha.100"
              height={logoSize}
              width={logoSize}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
                animation="shimmer 2s infinite"
              />
            </Box>

            {/* Text skeletons */}
            <VStack spacing={1} align="start">
              <Box
                position="relative"
                overflow="hidden"
                borderRadius="md"
                bg="whiteAlpha.100"
                height="14px"
                width="45px"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
                  animation="shimmer 2s infinite"
                  style={{ animationDelay: '0.1s' }}
                />
              </Box>
              <Box
                position="relative"
                overflow="hidden"
                borderRadius="md"
                bg="whiteAlpha.100"
                height="8px"
                width="35px"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
                  animation="shimmer 2s infinite"
                  style={{ animationDelay: '0.2s' }}
                />
              </Box>
            </VStack>
          </HStack>

          {/* Badge skeleton */}
          <Box
            position="relative"
            overflow="hidden"
            borderRadius="full"
            bg="whiteAlpha.100"
            height="18px"
            width="45px"
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
              animation="shimmer 2s infinite"
              style={{ animationDelay: '0.3s' }}
            />
          </Box>
        </HStack>

        {/* Balance skeleton */}
        <Box
          position="relative"
          overflow="hidden"
          borderRadius="lg"
          bg="whiteAlpha.100"
          height="55px"
          width="full"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
            animation="shimmer 2s infinite"
            style={{ animationDelay: '0.4s' }}
          />
        </Box>

        {/* Address skeleton */}
        <Box
          position="relative"
          overflow="hidden"
          borderRadius="md"
          bg="whiteAlpha.100"
          height="40px"
          width="full"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
            animation="shimmer 2s infinite"
            style={{ animationDelay: '0.5s' }}
          />
        </Box>

        {/* Buttons skeleton */}
        <HStack w="full" spacing={2}>
          <Box
            position="relative"
            overflow="hidden"
            borderRadius="lg"
            bg="whiteAlpha.100"
            height="36px"
            flex={1}
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
              animation="shimmer 2s infinite"
              style={{ animationDelay: '0.6s' }}
            />
          </Box>
          <Box
            position="relative"
            overflow="hidden"
            borderRadius="lg"
            bg="whiteAlpha.100"
            height="36px"
            flex={1}
          >
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
              animation="shimmer 2s infinite"
              style={{ animationDelay: '0.7s' }}
            />
          </Box>
        </HStack>
      </VStack>
    </Box>
  );
}

  if (error) {
    return (
      <Box
        borderRadius={{ base: "lg", md: "xl" }}
        p={cardPadding}
        bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
        border="1px solid"
        borderColor="red.500"
        maxW={cardMaxW}
        mx="auto"
      >
        <VStack spacing={2} py={3}>
          <Text fontSize="xl">⚠️</Text>
          <Text color="red.300" fontSize="xs" textAlign="center" fontWeight="semibold">
            {error}
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => !isMobile && setTilt({ x: 0, y: 0 })}
      style={{
        transform: isMobile ? "none" : `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
      }}
      transition="transform 0.15s ease-out"
      position="relative"
      borderRadius={{ base: "lg", md: "xl" }}
      p={cardPadding}
      bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.98))"
      border="1px solid"
      borderColor="whiteAlpha.200"
      boxShadow={`0 ${isMobile ? '6' : '12'}px ${isMobile ? '18' : '35'}px rgba(0,0,0,0.5), 0 0 ${isMobile ? '12' : '25'}px ${color.shadow}`}
      maxW={cardMaxW}
      mx="auto"
      overflow="hidden"
      _hover={{
        borderColor: "whiteAlpha.300",
        boxShadow: `0 ${isMobile ? '8' : '16'}px ${isMobile ? '22' : '42'}px rgba(0,0,0,0.6), 0 0 ${isMobile ? '16' : '32'}px ${color.shadow}`,
      }}
    >
      {/* Gradient border */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="2px"
        bgGradient={color.gradient}
        sx={{
          animation: 'shimmer 3s ease-in-out infinite',
          '@keyframes shimmer': {
            '0%, 100%': { opacity: 0.6 },
            '50%': { opacity: 1 }
          }
        }}
      />

      {/* Glow effect */}
      {!isMobile && (
        <Box
          position="absolute"
          inset="-2px"
          borderRadius="xl"
          bgGradient={color.gradient}
          opacity={0.08}
          filter="blur(18px)"
          zIndex={0}
          pointerEvents="none"
        />
      )}

      <VStack spacing={{ base: 1.5, sm: 2.5, md: 2 }} position="relative" zIndex={2}>
        {/* Header */}
        <HStack w="full" justify="space-between">
          <HStack spacing={{ base: 1.5, sm: 2, md: 2.5 }}>
            <Box
              bgGradient={color.gradient}
              boxSize={logoSize}
              borderRadius={{ base: "md", sm: "lg" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow={`0 0 ${isMobile ? '10' : '16'}px ${color.shadow}`}
            >
              <Image src={logoPath} alt="logo" boxSize="70%" />
            </Box>

            <VStack spacing={0} align="start">
              <Text fontSize={{ base: "sm", sm: "lg", md: "lg" }} fontWeight="bold" color="white">
                {wallet?.symbol || "..."}
              </Text>
              <Text fontSize={{ base: "8px", sm: "10px", md: "9px" }} color="whiteAlpha.500" fontWeight="semibold" letterSpacing="wide">
                WALLET
              </Text>
            </VStack>
          </HStack>

          <Badge
            bgGradient="linear(to-r, green.400, green.500)"
            color="white"
            borderRadius="full"
            px={{ base: 1, sm: 2, md: 1.5 }}
            py={{ base: 0, sm: 0.5 }}
            fontSize={{ base: "8px", sm: "10px", md: "9px" }}
            fontWeight="bold"
            boxShadow="0 0 8px rgba(72, 187, 120, 0.4)"
          >
            ● LIVE
          </Badge>
        </HStack>

        {/* Balance */}
        <Box
          bg="whiteAlpha.100"
          px={{ base: 2, sm: 3, md: 3 }}
          py={{ base: 1.5, sm: 2.5, md: 2.5 }}
          borderRadius={{ base: "md", sm: "lg" }}
          w="full"
          border="1px solid"
          borderColor="whiteAlpha.200"
          backdropFilter="blur(10px)"
          opacity={balanceRevealed ? 1 : 0}
          transform={balanceRevealed ? "translateY(0)" : "translateY(10px)"}
          transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)"
        >
          <VStack spacing={{ base: 1, sm: 0.5 }} align="stretch">
            <Text fontSize={{ base: "7px", sm: "10px", md: "9px" }} color="whiteAlpha.500" fontWeight="bold" letterSpacing="wide">
              BALANCE
            </Text>
            {/* Mobile: stacked layout */}
            <VStack spacing={0.5} align="stretch" display={{ base: "flex", sm: "none" }}>
              <HStack spacing={0.5} justify="center">
                <Text fontSize="md" fontWeight="black" color="white" lineHeight="1">
                  {walletData.amount.toFixed(4)}
                </Text>
                <Text fontSize="9px" color="whiteAlpha.600" fontWeight="semibold">
                  {wallet?.symbol}
                </Text>
              </HStack>
              <HStack spacing={1} justify="center" align="baseline">
                <Text fontSize="sm" fontWeight="bold" color="green.300" lineHeight="1">
                  ${walletData.totalUsd.toFixed(2)}
                </Text>
                <Text fontSize="7px" color="whiteAlpha.500">
                  @ ${walletData.price.toFixed(2)}
                </Text>
              </HStack>
               {/* ✅ ADAUGI AICI pentru Mobile */}
              {wallet?.change24h && Number(wallet.change24h) !== 0 && (
                <HStack spacing={1} justify="center" pt={0.5}>
                  <Text 
                    fontSize="8px" 
                    color={Number(wallet.change24h) > 0 ? "green.400" : "red.400"}
                    fontWeight="bold"
                  >
                    {Number(wallet.change24h) > 0 ? "↑" : "↓"} 
                    {Math.abs(Number(wallet.change24h)).toFixed(2)}%
                  </Text>
                  <Text fontSize="7px" color="whiteAlpha.400">24h</Text>
                </HStack>
              )}
            </VStack>
            {/* Tablet & Desktop: horizontal layout */}
            <HStack justify="space-between" align="baseline" display={{ base: "none", sm: "flex" }}>
              <HStack spacing={0.5}>
                <Text fontSize={{ sm: "xl", md: "xl" }} fontWeight="black" color="white" lineHeight="1">
                  {walletData.amount.toFixed(4)}
                </Text>
                <Text fontSize={{ sm: "xs", md: "2xs" }} color="whiteAlpha.600" fontWeight="semibold">
                  {wallet?.symbol}
                </Text>
              </HStack>
              <VStack spacing={0} align="end">
                <Text fontSize={{ sm: "xl", md: "xl" }} fontWeight="bold" color="green.300" lineHeight="1">
                  ${walletData.totalUsd.toFixed(2)}
                </Text>
                <Text fontSize={{ sm: "10px", md: "9px" }} color="whiteAlpha.500">
                  @ ${walletData.price.toFixed(2)}
                </Text>
                {wallet?.change24h && Number(wallet.change24h) !== 0 && (
                  <Badge
                    colorScheme={Number(wallet.change24h) > 0 ? "green" : "red"}
                    fontSize={{ sm: "9px", md: "8px" }}
                    px={1.5}
                    borderRadius="md"
                    fontWeight="bold"
                  >
                    {Number(wallet.change24h) > 0 ? "↑" : "↓"} 
                    {Math.abs(Number(wallet.change24h)).toFixed(2)}%
                  </Badge>
                )}
              </VStack>
            </HStack>
          </VStack>
        </Box>

        {/* Address */}
        <Tooltip label={address} hasArrow bg="gray.700" color="white" fontSize="xs">
          <Box
            bg="whiteAlpha.100"
            px={{ base: 2, sm: 3, md: 2.5 }}
            py={{ base: 1, sm: 2, md: 1.5 }}
            borderRadius="md"
            border="1px solid"
            borderColor="whiteAlpha.200"
            w="full"
            textAlign="center"
            backdropFilter="blur(10px)"
            cursor="pointer"
            _hover={{ bg: "whiteAlpha.150", borderColor: "whiteAlpha.300" }}
          >
            <Text fontSize={{ base: "7px", sm: "10px", md: "9px" }} color="whiteAlpha.400" mb={{ base: 0, sm: 0.5 }} fontWeight="bold" letterSpacing="wide">
              ADDRESS
            </Text>
            <Text fontFamily="monospace" fontSize={{ base: "9px", sm: "xs", md: "xs" }} color="whiteAlpha.800" fontWeight="semibold" lineHeight="1.2">
              {truncate(address)}
            </Text>
          </Box>
        </Tooltip>

        {/* Buttons */}
        <HStack w="full" spacing={{ base: 1, sm: 2 }}>
          <Button
            flex={1}
            bgGradient={color.gradient}
            color="white"
            fontWeight="bold"
            size={{ base: "xs", sm: "md", md: "sm" }}
            fontSize={{ base: "8px", sm: "sm", md: "xs" }}
            leftIcon={copied ? <CheckIcon boxSize={{ base: 2, sm: 3.5, md: 3 }} /> : <CopyIcon boxSize={{ base: 2, sm: 3.5, md: 3 }} />}
            onClick={copy}
            borderRadius={{ base: "md", sm: "lg" }}
            boxShadow={`0 3px 12px ${color.shadow}`}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: `0 5px 16px ${color.shadow}`,
            }}
            _active={{
              transform: "translateY(0)",
            }}
            isDisabled={copied}
            h={{ base: "30px", sm: "40px", md: "36px" }}
            minW={{ base: "70px", sm: "auto" }}
            px={{ base: 2, sm: 4 }}
          >
            {copied ? "COPIED" : "COPY"}
          </Button>

          {explorer !== "#" && (
            <Button
              flex={1}
              color={color.color}
              variant="outline"
              borderColor={color.color}
              size={{ base: "xs", sm: "md", md: "sm" }}
              fontSize={{ base: "8px", sm: "sm", md: "xs" }}
              as={Link}
              href={explorer}
              isExternal
              rightIcon={<ExternalLinkIcon boxSize={{ base: 2, sm: 3.5, md: 3 }} />}
              borderRadius={{ base: "md", sm: "lg" }}
              fontWeight="bold"
              _hover={{ bg: `${color.color}22`, transform: "translateY(-2px)" }}
              _active={{
                transform: "translateY(0)",
              }}
              h={{ base: "30px", sm: "40px", md: "36px" }}
              minW={{ base: "70px", sm: "auto" }}
              px={{ base: 2, sm: 4 }}
            >
              <Text as="span" display={{ base: "none", sm: "inline" }}>EXPLORER</Text>
              <Text as="span" display={{ base: "inline", sm: "none" }}>VIEW</Text>
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  );
}